import {
    ExtendPracticeDialog,
    FreePracticeDialog,
    RecapPracticeDialog,
} from '@/components/create-practice-dialog';
import ContentLayout from '@/components/layout/ContentLayout';
import {
    BigCardDecor,
    FreeCardDecor,
    RecapCardDecor,
} from '@/components/practice-decorations';
import { cn } from '@/lib/utils';
import { CircleAlert, SlidersHorizontal, Shapes } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface PracticeCardProps {
    onClick: () => void;
    isLoading?: boolean;
    className?: string;
    style?: React.CSSProperties;
    children: React.ReactNode;
}

function PracticeCardButton({ onClick, isLoading, className, style, children }: PracticeCardProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={isLoading}
            style={style}
            className={cn(
                /* @container：让卡内文字/内边距按「卡片自身宽度」做流体缩放（见 CardCopy） */
                '@container',
                /* flex-col + 顶对齐：修正 <button> 默认把块级子元素垂直居中的问题 */
                'relative flex w-full flex-col items-start overflow-hidden rounded-[50px] shadow-md text-left',
                'transition-transform duration-300 hover:scale-[1.01] active:scale-[0.99]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                'disabled:pointer-events-none disabled:opacity-75',
                className,
            )}
        >
            {children}
        </button>
    );
}

function CardCopy({
    icon,
    title,
    desc,
    fgVar,
    descMaxWidth,
}: {
    icon: React.ReactNode;
    title: string;
    desc: string;
    fgVar: string;
    descMaxWidth: number;
}) {
    return (
        <div
            className="relative z-10 flex flex-col"
            style={{
                color: fgVar,
                paddingTop: 'clamp(20px, 5.6cqw, 39px)',
                paddingLeft: 'clamp(22px, 8.19cqw, 57px)',
                paddingRight: 'clamp(16px, 3.45cqw, 24px)',
            }}
        >
            {icon}
            <h2
                className="font-bold leading-[1.05] tracking-tight"
                style={{ marginTop: 'clamp(14px, 3.16cqw, 22px)', fontSize: 'clamp(22px, 5.17cqw, 36px)' }}
            >
                {title}
            </h2>
            <p
                className="leading-[1.2]"
                style={{
                    marginTop: 'clamp(14px, 3.16cqw, 22px)',
                    fontSize: 'clamp(14px, 2.87cqw, 20px)',
                    opacity: 0.82,
                    maxWidth: descMaxWidth,
                }}
            >
                {desc}
            </p>
        </div>
    );
}

/* ── Big card: "更多練習" ── */
function BigCard({ onClick, isLoading, className }: { onClick: () => void; isLoading?: boolean; className?: string }) {
    const { t } = useTranslation();
    return (
        <PracticeCardButton
            onClick={onClick}
            isLoading={isLoading}
            className={className}
            style={{ backgroundColor: 'var(--practice-big-card)' }}
        >
            <CardCopy
                icon={<Shapes strokeWidth={1.8} style={{ color: 'var(--practice-big-card-fg)', width: 'clamp(26px, 5.46cqw, 38px)', height: 'clamp(26px, 5.46cqw, 38px)' }} />}
                title={t('component.exttitle')}
                desc={t('component.extdesc')}
                fgVar="var(--practice-big-card-fg)"
                descMaxWidth={265}
            />
            <BigCardDecor />
        </PracticeCardButton>
    );
}

/* ── Recap card: "錯題重練" ── */
function RecapCard({ onClick, isLoading, className }: { onClick: () => void; isLoading?: boolean; className?: string }) {
    const { t } = useTranslation();
    return (
        <PracticeCardButton
            onClick={onClick}
            isLoading={isLoading}
            className={className}
            style={{ backgroundColor: 'var(--practice-recap-card)' }}
        >
            <CardCopy
                icon={<CircleAlert strokeWidth={1.8} color="#BA1A1A" style={{ width: 'clamp(28px, 5.75cqw, 40px)', height: 'clamp(28px, 5.75cqw, 40px)' }} />}
                title={t('component.recaptitle')}
                desc={t('component.recapdesc')}
                fgVar="var(--practice-recap-card-fg)"
                descMaxWidth={272}
            />
            <RecapCardDecor />
        </PracticeCardButton>
    );
}

/* ── Free card: "自由主題" ── */
function FreeCard({ onClick, isLoading, className }: { onClick: () => void; isLoading?: boolean; className?: string }) {
    const { t } = useTranslation();
    return (
        <PracticeCardButton
            onClick={onClick}
            isLoading={isLoading}
            className={className}
            style={{ backgroundColor: 'var(--practice-free-card)' }}
        >
            <CardCopy
                icon={<SlidersHorizontal strokeWidth={1.8} style={{ color: 'var(--practice-free-card-fg)', width: 'clamp(28px, 5.75cqw, 40px)', height: 'clamp(28px, 5.75cqw, 40px)' }} />}
                title={t('component.freetitle')}
                desc={t('component.freedesc')}
                fgVar="var(--practice-free-card-fg)"
                descMaxWidth={259}
            />
            <FreeCardDecor />
        </PracticeCardButton>
    );
}

const PracticeHomePage = () => {
    const { t } = useTranslation();

    return (
        <ContentLayout title={t('student.practice')} backgroundColor="var(--practice-page-bg)" className="md:h-full md:overflow-hidden">
            {/* lg/xl 左 padding 降到 1920 的 pl-11(gap≈35,内容贴近侧栏)；2xl 锚 px-10 保持不变 */}
            <div className="mx-auto flex w-full max-w-[2000px] flex-1 flex-col px-6 pt-6 pb-6 lg:pl-[11px] lg:pr-10 2xl:px-10 min-[1920px]:pl-[11px]! md:min-h-0 md:overflow-y-auto">
                {/* 卡片簇：md+ 撑满可用宽高，横向 696:703 分配 */}
                <div className="flex min-h-0 flex-1 flex-col gap-5 md:flex-row md:items-stretch md:gap-6">
                    {/* 大卡 —— 更多練習 */}
                    <div className="md:min-h-0 md:flex-[696]">
                        <ExtendPracticeDialog
                            renderTrigger={({ onClick, isLoading }) => (
                                <BigCard onClick={onClick} isLoading={isLoading} className="aspect-[696/817] md:aspect-auto md:h-full" />
                            )}
                        />
                    </div>

                    {/* 右列：Recap 上、Free 下；两卡平分高度，保持 703/397 比例 */}
                    <div className="flex flex-col gap-5 md:min-h-0 md:flex-[703] md:gap-6">
                        <RecapPracticeDialog
                            renderTrigger={({ onClick, isLoading }) => (
                                <RecapCard
                                    onClick={onClick}
                                    isLoading={isLoading}
                                    className="aspect-[703/397] md:aspect-auto md:h-full"
                                />
                            )}
                        />
                        <FreePracticeDialog
                            renderTrigger={({ onClick, isLoading }) => (
                                <FreeCard
                                    onClick={onClick}
                                    isLoading={isLoading}
                                    className="aspect-[703/397] md:aspect-auto md:h-full"
                                />
                            )}
                        />
                    </div>
                </div>
            </div>
        </ContentLayout>
    );
};

export default PracticeHomePage;
